// ============================================================================
// checklistStatusWriter — Status promotion, delta application, and completion
// writes for checklist items.
// ----------------------------------------------------------------------------
// Extracted to break the circular ESM dependency between
// checklistMutationService and checklistDefinitionService.
// ============================================================================

import type {
  ChecklistItem,
  ChecklistWorkflowStatus,
} from '../types/schema'
import type { ChecklistCanonicalActivityAction } from './checklistDefinitionService'
import {
  ChecklistBlockerError,
  createDeltaOperationId,
} from './stripboardDeltaService'
import type { StripboardDelta, StripboardDeltaResult, BoardStatus } from '../types/stripboard'
import { bookIdFor } from '../types/checklistBooks'
import {
  getPage,
  pageKeyFor,
  setChecklistCompletionOnPage,
} from './checklistBookService'
import { projectLegacyChecklistTypeToNew } from './checklistViewAdapter'
import { workflowStatusToChecklistBoardStatus } from './checklistStatusMachine'
import { workspaceTodayKey } from './repeat/keys'
import { effectivePeriodBoardStatus } from './repeat/completions'

export { workflowStatusToChecklistBoardStatus } from './checklistStatusMachine'

export interface ChecklistMutationActor {
  id?: string | null
  name?: string | null
}

export type ChecklistDeltaRunner = (delta: StripboardDelta) => Promise<StripboardDeltaResult>

export const fetchWorkspaceRawTimezone = async (workspaceId: string): Promise<string | undefined> => {
  if (!workspaceId) return undefined
  try {
    const [{ doc, getDocFromServer }, { firestoreDb }] = await Promise.all([
      import('firebase/firestore'),
      import('./firebase'),
    ])
    const snap = await getDocFromServer(doc(firestoreDb, 'workspaces', workspaceId))
    if (!snap.exists()) return undefined
    return snap.data()?.timezone as string | undefined
  } catch {
    return undefined
  }
}

export interface UpdateChecklistWorkflowStatusInput {
  item: Pick<ChecklistItem, 'id'>
    & Partial<Pick<ChecklistItem, 'boardStatus' | 'recordVersion' | 'workflowStatus' | 'workspaceId' | 'checklistType' | 'previousWorkflowStatus' | 'notRequiredFrom'>>
  workflowStatus: ChecklistWorkflowStatus | null | undefined
  coordinates?: { lat: number; lng: number } | null
  runDelta: ChecklistDeltaRunner
  /** Present in book-model callers so the write also lands on the page.
   *  Legacy callers may omit; legacy delta path still runs. */
  actor?: ChecklistMutationActor
  pageKey?: string | null
  periodKey?: string | null
  periodBoardStatus?: BoardStatus | null
  workspaceTimeZone?: string | null
  notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null
}

export const isChecklistHelperVisible = (value: unknown): boolean => value !== false

export const workflowStatusIsCompleted = (
  status: ChecklistWorkflowStatus | null | undefined,
): boolean => status === 'COMPLETED'

export const checklistStatusActivityFor = (
  previousStatus: ChecklistWorkflowStatus | null | undefined,
  nextStatus: ChecklistWorkflowStatus | null | undefined,
): ChecklistCanonicalActivityAction => {
  if (nextStatus === 'COMPLETED') return 'CHECKLIST_COMPLETED'
  if (previousStatus === 'COMPLETED') return 'CHECKLIST_REOPENED'
  return 'CHECKLIST_STATUS_CHANGED'
}

export const isChecklistWorkflowStatus = (value: unknown): value is ChecklistWorkflowStatus =>
  value === 'CREATED' ||
  value === 'ASSIGNED' ||
  value === 'ASSESSING' ||
  value === 'ACCEPTED' ||
  value === 'ONGOING' ||
  value === 'COMPLETED' ||
  value === 'NOT_REQUIRED' ||
  value === 'POSTPONED'

export interface UpdateChecklistWorkflowStatusResult extends Omit<StripboardDeltaResult, 'workflowStatus'> {
  workflowStatus: ChecklistWorkflowStatus | null
}

export const updateChecklistWorkflowStatus = async (
  input: UpdateChecklistWorkflowStatusInput,
): Promise<UpdateChecklistWorkflowStatusResult> => {
  const rawType = input.item.checklistType
  const type = projectLegacyChecklistTypeToNew(rawType)
  const isRepeating = type === 'DAILY' || type === 'WEEKLY'

  let resolvedPeriodKey = input.periodKey ?? input.pageKey ?? null
  if (isRepeating && !resolvedPeriodKey) {
    let rawTz = input.workspaceTimeZone
    if (rawTz === undefined && input.item.workspaceId) {
      rawTz = await fetchWorkspaceRawTimezone(input.item.workspaceId)
    }
    resolvedPeriodKey = workspaceTodayKey(type, rawTz)
  }

  const bookId = input.item.workspaceId ? bookIdFor(input.item.workspaceId, type) : null

  let initialSourceStatus: BoardStatus | null = input.item.boardStatus ?? null
  if (isRepeating) {
    if (input.periodBoardStatus !== undefined) {
      initialSourceStatus = input.periodBoardStatus
    } else if (bookId && resolvedPeriodKey) {
      const page = await getPage(bookId, resolvedPeriodKey)
      const entry = page?.completions?.[input.item.id]
      initialSourceStatus = (effectivePeriodBoardStatus(entry, input.item, resolvedPeriodKey) as BoardStatus)
    } else {
      initialSourceStatus = 'TODO'
    }
  }

  const runOnce = async (params: {
    id: string
    boardStatus: BoardStatus | null
    recordVersion: number
  }) =>
    input.runDelta({
      operationId: createDeltaOperationId('checklist-status', params.id),
      operationType: 'STATUS_MOVE',
      recordId: params.id,
      recordKind: 'CHECKLIST',
      sourceStatus: params.boardStatus,
      destinationStatus: workflowStatusToChecklistBoardStatus(input.workflowStatus),
      expectedVersion: params.recordVersion,
      checklistWorkflowStatus: input.workflowStatus,
      coordinates: input.coordinates ?? null,
      ...(isRepeating && resolvedPeriodKey ? { periodKey: resolvedPeriodKey } : {}),
      ...(input.notRequiredScope ? { notRequiredScope: input.notRequiredScope } : {}),
    })

  let result: StripboardDeltaResult
  try {
    result = await runOnce({
      id: input.item.id,
      boardStatus: initialSourceStatus,
      recordVersion: input.item.recordVersion ?? 1,
    })
  } catch (err) {
    const isVersionConflict = err instanceof ChecklistBlockerError
      ? false // blocker errors bubble
      : (err as { details?: { code?: string } })?.details?.code === 'VERSION_CONFLICT'
        || ((err as { code?: string })?.code === 'functions/aborted' || (err as { code?: string })?.code === 'aborted')
          && /source lane|record changed/i.test(String((err as { message?: string })?.message))
        || /source lane|record changed/i.test(String((err as { message?: string })?.message))

    if (!isVersionConflict) throw err

    if (isRepeating) {
      const { refetchChecklistItemHead } = await import('./checklistReader')
      const [freshHead, freshPage] = await Promise.all([
        refetchChecklistItemHead(input.item.id),
        bookId && resolvedPeriodKey ? getPage(bookId, resolvedPeriodKey) : null,
      ])
      if (!freshHead) throw err
      const freshEntry = freshPage?.completions?.[input.item.id]
      const freshStatus = (effectivePeriodBoardStatus(freshEntry, freshHead, resolvedPeriodKey) as BoardStatus)
      const freshWorkflow = freshEntry?.workflowStatus as ChecklistWorkflowStatus | undefined

      if (freshWorkflow === input.workflowStatus) {
        result = {
          operationId: createDeltaOperationId('checklist-status', input.item.id),
          recordId: input.item.id,
          recordVersion: freshHead.recordVersion,
          boardStatus: freshStatus,
          workflowStatus: freshWorkflow ?? null,
          invalidDependencyNodeIds: [],
          dependencyAlertNodeIds: [],
          periodKey: resolvedPeriodKey,
        }
      } else {
        result = await runOnce({
          id: input.item.id,
          boardStatus: freshStatus,
          recordVersion: freshHead.recordVersion,
        })
      }
    } else {
      // Refetch fresh state from Firestore and retry once
      const { refetchChecklistItemHead } = await import('./checklistReader')
      const fresh = await refetchChecklistItemHead(input.item.id)
      if (!fresh) throw err

      if (fresh.workflowStatus === input.workflowStatus) {
        result = {
          operationId: createDeltaOperationId('checklist-status', input.item.id),
          recordId: input.item.id,
          recordVersion: fresh.recordVersion,
          boardStatus: fresh.boardStatus,
          workflowStatus: fresh.workflowStatus,
          invalidDependencyNodeIds: [],
          dependencyAlertNodeIds: [],
        }
      } else {
        result = await runOnce({
          id: input.item.id,
          boardStatus: fresh.boardStatus,
          recordVersion: fresh.recordVersion,
        })
      }
    }
  }

  if (!isRepeating && input.actor && input.item.workspaceId) {
    const isCompleted = input.workflowStatus === 'COMPLETED'
    const nowIso = new Date().toISOString()
    let tz = input.workspaceTimeZone
    if (tz === undefined && input.item.workspaceId) {
      tz = await fetchWorkspaceRawTimezone(input.item.workspaceId)
    }
    const pageKey = input.pageKey ?? pageKeyFor(type, new Date(), tz ?? undefined)
    const bookActor = { uid: input.actor.id ?? '', displayName: input.actor.name ?? undefined }

    if (bookActor.uid && bookId) {
      await setChecklistCompletionOnPage(
        bookId,
        pageKey,
        input.item.id,
        {
          workflowStatus: input.workflowStatus,
          previousWorkflowStatus: input.item.workflowStatus ?? null,
          boardStatus: workflowStatusToChecklistBoardStatus(input.workflowStatus),
          isCompleted,
          completedAt: isCompleted ? nowIso : null,
          completedById: isCompleted ? (input.actor.id ?? null) : null,
          completedByName: isCompleted ? (input.actor.name ?? null) : null,
          ...(input.coordinates ? { verifiedCoordinates: input.coordinates } : {}),
        },
        bookActor,
      ).catch((err) => console.warn('[checklistStatusWriter] book-model completion write failed', err))
    }
  }

  const resolvedWorkflowStatus: ChecklistWorkflowStatus | null =
    result.workflowStatus === null
      ? null
      : isChecklistWorkflowStatus(result.workflowStatus)
        ? result.workflowStatus
        : (input.workflowStatus ?? null)

  return {
    ...result,
    workflowStatus: resolvedWorkflowStatus,
  }
}
